import { createSelector } from '@reduxjs/toolkit';

import { Sector } from '@/domain/impact/impact.entity';
import {
  Category,
  ImpactCo2Answer,
} from '@/domain/impact-co2/entities/question.entity';
import { RootState } from '@/domain/store';

import { selectValidatedExternalInvitationsForSector } from '../external-invitation/external-invitations.selectors';

export const selectAllImpactCo2Answers = (
  state: RootState,
): ImpactCo2Answer[] => {
  return state.impactCo2.answers;
};

//TODO maybe rename to selectImpactCo2Answers My answers ?

/**
 * Use generic type to specify the type of values returned
 *
 * Call example :
 * selectImpactCo2Answers<ValuesMobility>({
 *    sector: 'TEAM',
 *    category: 'MOBILITY',
 * })
 * => retruned object: ImpactCo2AnswerOfType<ValuesMobility>[]
 */
export const selectImpactCo2Answers = <T>({
  sector,
  category,
  isExternal,
}: {
  sector: Sector;
  category: Category;
  isExternal: boolean;
}) =>
  createSelector([selectAllImpactCo2Answers], (answers) => {
    const filteredAnswers = answers.filter(
      (x) =>
        x.sector === sector &&
        x.category === category &&
        x.isExternal === isExternal &&
        x.source !== 'IMPORT_THEGREENSHOT',
    );
    return filteredAnswers as ImpactCo2Answer<T>[];
  });

// version with createSelector that create a warning in terminal
// did not fix so I used create a more basic function

// export const selectImpactCo2AnswersExternalAndValidated = <T>({
//   sector,
//   category,
// }: {
//   sector: Sector;
//   category: Category;
// }) => {
//   return createSelector(
//     [
//       selectImpactCo2Answers({ sector, category, isExternal: true }),
//       selectValidatedExternalInvitationsForSector(sector),
//     ],
//     (answers, externalInvitations) => {
//     ....

export const selectImpactCo2AnswersExternalAndValidated =
  <T>({ sector, category }: { sector: Sector; category: Category }) =>
  (state: RootState): ImpactCo2Answer<T>[] => {
    const answers = selectImpactCo2Answers<T>({
      sector,
      category,
      isExternal: true,
    })(state);
    const externalInvitations =
      selectValidatedExternalInvitationsForSector(sector)(state);
    const validatedUserIds = externalInvitations.map((x) => x.code);
    const filteredAnswers = answers.filter((x) =>
      validatedUserIds.includes(x.userId),
    );
    return filteredAnswers as ImpactCo2Answer<T>[];
  };

export const selectAllImpactCo2AnswersImported = (
  state: RootState,
): ImpactCo2Answer[] => {
  const answers = selectAllImpactCo2Answers(state);
  const filteredAnswers = answers.filter(
    (x) => x.source === 'IMPORT_THEGREENSHOT',
  );
  return filteredAnswers;
};

export const selectImpactCo2AnswersImported =
  <T>({ sector, category }: { sector: Sector; category: Category }) =>
  (state: RootState): ImpactCo2Answer<T>[] => {
    const answers = selectAllImpactCo2Answers(state);
    const filteredAnswers = answers.filter(
      (x) =>
        x.sector === sector &&
        x.category === category &&
        x.isExternal === false &&
        x.source === 'IMPORT_THEGREENSHOT',
    );
    return filteredAnswers as ImpactCo2Answer<T>[];
  };

export const selectImpactCo2AnswersImportedForCategories =
  ({ sector, categories }: { sector: Sector; categories: Category[] }) =>
  (state: RootState): ImpactCo2Answer[] => {
    const answers = selectAllImpactCo2Answers(state);
    const filteredAnswers = answers.filter(
      (x) =>
        x.sector === sector &&
        categories.includes(x.category) &&
        x.isExternal === false &&
        x.source === 'IMPORT_THEGREENSHOT',
    );
    return filteredAnswers;
  };

export const selectIsFetchingImpactCo2Answers = (state: RootState) => {
  return state.impactCo2.isFetchingAnswers;
};

export const selectIsSubmitingImpactCo2Answer = (state: RootState) => {
  return state.impactCo2.isSubmitingAnswer;
};

export const selectIsDeletingImpactCo2Answer = (state: RootState) => {
  return state.impactCo2.isDeletingAnswer;
};

export const selectIsImportingImpactCo2Answers = (state: RootState) => {
  return state.impactCo2.isImportingAnswers;
};
